import { createContext, useContext, useEffect, useRef, useState } from "react";

import Pusher from "pusher-js";
import { useSelector } from "react-redux";

const OnlineStatusContext = createContext();

const OnlineStatusProvider = ({ children }) => {
  const [onlineGroups, setOnlineGroups] = useState([]);
  const onlineGroupsRef = useRef([]);

  const [pusherInit, setPusherInit] = useState(false);
  const pusherRef = useRef(null);

  const [channelInit, setChannelInit] = useState(false);
  const channelRef = useRef(null);

  const userId = useSelector((state) => {
    return state.Auth?.user_id || null;
  });
  const authToken = useSelector((state) => {
    return state.Auth?.access_token || null;
  });

  const pusherKey = process.env.REACT_APP_PWS_API_KEY;
  const pusherCluster = process.env.REACT_APP_PWS_CLUSTER_REGION;
  const pusherAuthEndpoint =
    new URL(process.env.REACT_APP_BASEURL).origin + "/broadcasting/auth";
  const pusherPresenceChannel = "presence-online";

  useEffect(() => {
    if (!userId) {
      return;
    }

    if (!authToken) {
      return;
    }

    if (pusherRef.current) {
      return;
    }

    if (channelRef.current) {
      return;
    }

    initPusher();

    return () => {
      resetContext();
    };
  }, [userId, authToken, pusherInit, channelInit]);

  const initPusher = () => {
    pusherRef.current = new Pusher(pusherKey, {
      cluster: pusherCluster,
      forceTLS: true,
      disableStats: true,
      enabledTransports: ["ws"], // ← WebSocket only
      authEndpoint: pusherAuthEndpoint,
      auth: {
        headers: {
          Authorization: `Bearer ${authToken}`,
        },
      },
    });

    // pusherRef.current.connection.bind("connected", () => {
    //   console.log("[CONNECTED] WebSocket established");
    // });

    // pusherRef.current.connection.bind("disconnected", () => {
    //   console.warn("[DISCONNECTED] WebSocket disconnected");
    //   resetContext();
    // });

    channelRef.current = pusherRef.current.subscribe(pusherPresenceChannel);

    // channelRef.current.bind("pusher:subscription_error", (status) => {
    //   console.error("[SUB ERROR]", status);
    //   resetContext();
    // });

    channelRef.current.bind("pusher:subscription_succeeded", (members) => {
      members.each((member) => {
        // console.log("[MEMBER ONLINE]", member.info);
        addGroup(member.info);
      });
    });

    channelRef.current.bind("pusher:member_added", (member) => {
      // console.log("[MEMBER ADDED]", member.info);
      addGroup(member.info);
    });

    channelRef.current.bind("pusher:member_removed", (member) => {
      // console.log("[MEMBER REMOVED]", member.info);
      removeGroup(member.info);
    });
  };

  const addGroup = (group) => {
    if (group.id === userId) {
      return;
    }

    if (isOnlineById(group.id)) {
      removeGroupById(group.id);
    }

    if (isOnlineByName(group.name)) {
      removeGroupByName(group.name);
    }

    onlineGroupsRef.current.push(group);

    setOnlineGroups((prev) => {
      return [...onlineGroupsRef.current];
    });
  };

  const removeGroup = (group) => {
    if (isOnlineById(group.id)) {
      removeGroupById(group.id);
    }

    if (isOnlineByName(group.name)) {
      removeGroupByName(group.name);
    }
  };

  const removeGroupById = (id) => {
    onlineGroupsRef.current = onlineGroupsRef.current.filter((group) => {
      return group.id !== id;
    });

    setOnlineGroups((prev) => {
      return [...onlineGroupsRef.current];
    });
  };
  const removeGroupByName = (name) => {
    onlineGroupsRef.current = onlineGroupsRef.current.filter((group) => {
      return group.name !== name;
    });

    setOnlineGroups((prev) => {
      return [...onlineGroupsRef.current];
    });
  };
  const isOnlineById = (id) => {
    return onlineGroupsRef.current.find((group) => group.id === id);
  };
  const isOnlineByName = (name) => {
    return onlineGroupsRef.current.find((group) => group.name === name);
  };
  const resetContext = () => {
    onlineGroupsRef.current = [];

    if (channelRef.current) {
      channelRef.current.unbind_all();
      channelRef.current.unsubscribe();
      channelRef.current = null;
    }

    if (pusherRef.current) {
      pusherRef.current.disconnect();
      pusherRef.current = null;
    }

    setOnlineGroups([]);
    setPusherInit(false);
    setChannelInit(false);
  };

  return (
    <OnlineStatusContext.Provider
      value={{
        onlineGroups,
        isOnlineById,
        isOnlineByName,
      }}
    >
      {children}
    </OnlineStatusContext.Provider>
  );
};

export const useOnlineStatusContext = () => {
  const onlineStatus = useContext(OnlineStatusContext);

  if (onlineStatus === undefined) {
    return console.error(
      "useOnlineStatusContext must be used with a OnlineStatusContext and OnlineStatusProvider!",
      404
    );
  }

  return onlineStatus;
};

export { OnlineStatusProvider, OnlineStatusContext };
